import { Component, DestroyRef, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { IManagementGroup, IPreferences, IProfile, IResponse } from '@interfaces/index';
import { ConsentService } from '@ui-components/consent';
import { AdminAccessService, AppRoutingService, AppService, AuthorizationService, ManagementGroupService, SessionService } from '@ui-components/services';
import { LibManagementGroupModalComponent } from '@ui-components/management-group-modal';
import { LibPreferencesModalComponent } from '@ui-components/preferences-modal';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'lib-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class LibHeaderComponent {
  @Input() home: string = '/';
  @Input() settings: string = '/manage-account/allotments';
  @Input() show_settings = true;
  @Input() profile: string = '/profile';
  @Input() external: boolean = false;
  @Input() ollie: string | null = null;
  @Input() show_admin_entry = false;

  currentUrl: string = '';
  currentPath: string = '';
  userProfile: IProfile | null = null;
  menuVisible: boolean = false;

  managementGroups: IManagementGroup[] = [];
  selectedGroup: IManagementGroup | null = null;
  isSuperSuperAdmin: boolean = false;

  showBanner: boolean = false;

  isAdmin = false;
  adminUrl = '';

  constructor(
    protected _app_routing_service: AppRoutingService,
    protected _session_service: SessionService,
    protected _app_service: AppService,
    protected _router: Router,
    protected _authorization_service: AuthorizationService,
    protected _management_group_service: ManagementGroupService,
    protected _admin_access_service: AdminAccessService,
    protected _consent_service: ConsentService,
    public dialog: MatDialog,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    if (this.show_admin_entry) {
      this.loadAdminAccess();
    }

    this._app_service.getProfile()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(error => {
          console.error('Error fetching user profile:', error);
          this.showErrorNotification('Failed to load user profile. Please try again later.');
          return of(null);
        })
      )
      .subscribe((response: IResponse<IProfile> | null) => {
        if (response && response.data) {
          this.userProfile = response.data;
          this._app_service.loading = false;
          this.currentUrl = window.location.href;
          this.currentPath = this._router.url;

          this.isSuperSuperAdmin = this._authorization_service.isSuperSuperAdmin(this.userProfile);

          if (this.isSuperSuperAdmin) {
            this.fetchManagementGroups();
          }
        } else {
          console.error('Failed to fetch user profile: Response is null or invalid.');
          this.showErrorNotification('Failed to load user profile. Please try again later.');
        }
      });
  }

  openManagementGroupModal(): void {
    const dialogRef = this.dialog.open(LibManagementGroupModalComponent, {
      width: '400px',
      data: {
        managementGroups: this.managementGroups,
        selectedGroup: this.selectedGroup
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedGroup: IManagementGroup | null) => {
        if (selectedGroup) {
          this.onGroupChange(selectedGroup);
        }
      });
  }

  fetchManagementGroups(): void {
    this._management_group_service.getManagementGroupsForAdmin()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(error => {
          console.error('Error fetching management groups:', error);
          this.showErrorNotification('Failed to load management groups. Please try again later.');
          return of([]);
        })
      )
      .subscribe((response: IResponse<IManagementGroup[]> | never[]) => {
        if (Array.isArray(response)) {
          console.error('Failed to fetch management groups: empty response.');
          this.showErrorNotification('Failed to load management groups. Please try again later.');
        } else if (response.code === 200) {
          this.managementGroups = response.data;
          this._app_service.getSelectedManagementGroupFromCookies(this.managementGroups);

          this._app_service.selectedManagementGroup$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((selectedGroup) => {
              if (selectedGroup) {
                this.selectedGroup = selectedGroup;
                this.checkForBannerDisplay();
              } else {
                this.setFallbackToUserOrg();
              }
            });
        } else {
          console.error('Failed to fetch management groups', response.message);
          this.showErrorNotification('Failed to load management groups.');
        }
      });
  }

  setFallbackToUserOrg(): void {
    const userOrgGroup = this.managementGroups.find(
      mg => mg.id === this.userProfile?.org_id
    );

    if (userOrgGroup) {
      this.selectedGroup = userOrgGroup;
      this.checkForBannerDisplay();
    } else {
      console.error('User organization not found in the management group list');
      this.showErrorNotification('User organization not found in management group list.');
    }
  }

  onGroupChange(group: IManagementGroup | null): void {
    if (group) {
      this._app_service.setSelectedManagementGroup(group);
      this.selectedGroup = group;
    } else {
      this._app_service.clearSelectedManagementGroup();

      const userOrgGroup = this.managementGroups.find(
        mg => mg.id === this.userProfile?.org_id
      );

      if (userOrgGroup) {
        this.selectedGroup = userOrgGroup;
      } else {
        console.error('User organization not found in the management group list');
        this.showErrorNotification('User organization not found in management group list.');
      }
    }

    this.checkForBannerDisplay();
  }

  checkForBannerDisplay(): void {
    if (this.selectedGroup?.id !== this.userProfile?.org_id) {
      this.showBanner = true;
    } else {
      this.showBanner = false;
    }
  }

  dismissBanner(): void {
    this.showBanner = false;
  }

  goToAssessments() {
    this._app_routing_service.goToAssessments();
  }

  goToAccount() {
    this._app_routing_service.goToAssessments('manage-account');
  }

  goToProfile() {
    this._app_routing_service.goToAssessments('profile');
  }

  goToOllie() {
    this._app_routing_service.goToOllie();
  }

  showPreferencesModal(preferences: IPreferences | null = null) {
    const dialog_ref = this.dialog.open(LibPreferencesModalComponent, {
      width: '700px',
      data: preferences
    });
  }

  openCookieSettings() {
    this._consent_service.reset();
  }

  logout() {
    this._session_service.logout();
  }

  goToHelpLink(): void {
    window.open(environment.help_link, '_blank');
  }

  toggleMenu() {
    this.menuVisible = !this.menuVisible;
    const navbar = document.querySelector('.navbar');
    if (navbar) {
      navbar.classList.toggle('active', this.menuVisible);
    }
  }

  showErrorNotification(message: string): void {
    this._app_service.notify(message, 'Close');
  }

  private loadAdminAccess(): void {
    this.adminUrl = this._app_routing_service.getAdminUrl();
    this._admin_access_service.isAdmin()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isAdmin) => {
        this.isAdmin = isAdmin;
      });
  }
}
